"use client";

import React, { useState } from "react";
import Image from "next/image";
import { MediaItem } from "@/type/commonTypes";
import { cleanExcerpt } from "./MediaFeaturedCard";
import Icon from "@/components/icons/Icons";

interface MediaCardProps {
  item: MediaItem;
}

export default function MediaCard({ item }: MediaCardProps) {
  const [imgError, setImgError] = useState(false);
  const excerpt = cleanExcerpt(item.content);

  const fallbackImage = "/next_assets/images/careers/team_cheering.png";
  const isVideo = item.linkType === "video";

  const CardContent = (
    <div className="bg-white rounded-2xl border border-[#EAECF0] shadow-xs hover:shadow-md transition overflow-hidden flex flex-col group h-full cursor-pointer">
      {/* Thumbnail */}
      <div className="relative w-full h-200 overflow-hidden bg-gray-100">
        <Image
          src={imgError || !item.imageUrl ? fallbackImage : item.imageUrl}
          alt={item.title || "Media Coverage"}
          width={600}
          height={600}
          className="object-cover group-hover:scale-105 transition-transform duration-500"
          onError={() => setImgError(true)}
        />
        {isVideo && (
          <div className="absolute inset-0 bg-black/20 flex items-center justify-center group-hover:bg-black/30 transition-colors">
            <div className="w-11 h-11 rounded-full bg-white/95 text-primaryBlue flex items-center justify-center shadow-md transform group-hover:scale-110 transition-transform">
              <svg
                className="w-5 h-5 ml-0.5"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-4 sm:p-6 flex-1 flex flex-col justify-between">
        {/* Badge & Date */}
        <div className="flex items-center justify-between gap-2 mb-3">
          <span
            className={[
              "inline-block px-2.5 py-0.5 text-xs font-semibold tracking-wide uppercase rounded-sm",
              isVideo
                ? "bg-[#FEF3F2] text-[#D92D20]"
                : "bg-[#EEF4FF] text-primaryBlue",
            ].join(" ")}
          >
            {isVideo ? "VIDEO" : "MEDIA"}
          </span>
          {item.postedOn && (
            <span className="text-xs text-[#667085] font-medium ml-auto">
              {item.postedOn}
            </span>
          )}
        </div>

        {/* Title */}
        <div className="font-bold text-2sm sm:text-base text-primaryBlack group-hover:text-primaryBlue transition line-clamp-2 mb-2.5">
          {item.title}
        </div>

        {/* Excerpt */}
        {excerpt && (
          <p className="text-2xs text-[#667085] line-clamp-2 leading mb-4">
            {excerpt}
          </p>
        )}

        {/* Action Link */}
        {item.href && (
          <p className="text-primaryBlue font-semibold text-xs sm:text-sm inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
            {item.linkText || (isVideo ? "Watch Video" : "Read More")}
            <Icon.rightArrow />
          </p>
        )}
      </div>
    </div>
  );

  if (item.href) {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        className="block h-full focus:outline-hidden focus:ring-2 focus:ring-primaryBlue rounded-2xl"
      >
        {CardContent}
      </a>
    );
  }

  return CardContent;
}
